/**
 * Opportunistic device-geolocation re-capture (phase 8 of the
 * device-geolocation moderation-signal program).
 *
 * On authed page loads, when the browser's geolocation permission is ALREADY
 * granted, silently capture one fix and POST it as a non-signup observation.
 * Never prompts: the permission state is checked with the Permissions API and
 * anything other than 'granted' is a no-op. Browsers without the Permissions
 * API (older Safari) are skipped entirely rather than risking a prompt.
 *
 * Scope: the main layout renders a `geo-observation` meta tag to opt its pages
 * into this module. Funnel/signup pages (_funnel.twig) render their own head
 * without it on purpose: the signup wizard has its own capture path, so the
 * opportunistic module stays inert there.
 *
 * Throttle: at most one capture attempt per user per THROTTLE_MS, tracked in
 * localStorage keyed by user id. The timestamp is written before the async
 * capture starts, which bounds repeated page loads; tabs that fire load
 * events near-simultaneously can still each capture once (localStorage has
 * no atomic check-and-set), and the endpoint's per-user rate limit bounds
 * that race.
 *
 * Everything is wrapped so a failure here can never break page load.
 */

import apiClient from './api-client.js';

const THROTTLE_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_KEY_PREFIX = 'geoObservation:lastCapture:';
const POSITION_TIMEOUT_MS = 15000;
// A fix from the last hour is fresh enough for a moderation observation and
// avoids spinning up positioning hardware when the browser has one cached.
const POSITION_MAX_AGE_MS = 60 * 60 * 1000;

/**
 * Attempt one opportunistic capture. Safe to call on every page load.
 * Fire-and-forget: never throws, never rejects.
 */
export async function maybeCaptureGeoObservation() {
    try {
        if (!document.querySelector('meta[name="geo-observation"]')) {
            return;
        }

        const userIdMeta = document.querySelector('meta[name="user-id"]');
        const userId = userIdMeta && userIdMeta.getAttribute('content');
        if (!userId) {
            return;
        }

        if (!('geolocation' in navigator) || !navigator.permissions || !navigator.permissions.query) {
            return;
        }

        const storageKey = STORAGE_KEY_PREFIX + userId;
        if (!claimThrottleSlot(storageKey)) {
            return;
        }

        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state !== 'granted') {
            return;
        }

        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: POSITION_TIMEOUT_MS,
                maximumAge: POSITION_MAX_AGE_MS,
                enableHighAccuracy: false
            });
        });

        await apiClient.postGeoObservation(
            {
                permission_status: 'granted',
                lat: position.coords.latitude,
                lon: position.coords.longitude,
                accuracy: position.coords.accuracy
            },
            { signup_context: false }
        );
    } catch {
        // Denied-after-query races, positioning failures, storage or network
        // errors: all intentionally swallowed. The throttle slot stays
        // claimed so a persistent failure retries on the next window, not on
        // every page load.
    }
}

/**
 * True when no capture ran within the throttle window. Claims the slot by
 * writing the current timestamp before any async work, so within one tab a
 * window admits a single attempt. The read-then-write is not atomic across
 * tabs (see the module docblock for why that is acceptable).
 */
function claimThrottleSlot(storageKey) {
    try {
        const last = parseInt(window.localStorage.getItem(storageKey), 10);
        if (!Number.isNaN(last) && Date.now() - last < THROTTLE_MS) {
            return false;
        }
        window.localStorage.setItem(storageKey, String(Date.now()));
        return true;
    } catch {
        // localStorage unavailable (private mode, quota): without a throttle
        // we must not capture on every page load.
        return false;
    }
}
